import { useCallback, useEffect, useMemo, useState } from "react";

import {
  createDuel,
  fetchDuelOptions,
  fetchDuels,
  markDuelNotificationsRead,
  performDuelAction,
} from "./api";
import { navigate } from "./navigation";
import type {
  DuelGame,
  DuelItem,
  DuelListResponse,
  DuelOptions,
  MemoDifficulty,
} from "./types";

const dateFormatter = new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatDate(value: string | null): string {
  return value ? dateFormatter.format(new Date(value)) : "—";
}

function resultLabel(duel: DuelItem): string | null {
  if (duel.resolution === "DRAW") return "Égalité parfaite";
  if (duel.winner_id === null) return null;
  const winner = duel.winner_id === duel.challenger.id ? duel.challenger : duel.opponent;
  return `${winner.display_name} remporte le duel${duel.resolution === "FORFEIT" ? " par forfait" : ""}.`;
}

function DuelCard({
  duel,
  busy,
  act,
}: {
  duel: DuelItem;
  busy: boolean;
  act: (duel: DuelItem, action: "accept" | "refuse" | "abandon") => void;
}) {
  const result = resultLabel(duel);
  const deadline = duel.play_deadline ?? duel.acceptance_deadline;
  return (
    <article className="duel-card">
      <div className="duel-card-heading">
        <div>
          <span className={`duel-status status-${duel.status.toLowerCase()}`}>
            {duel.status_label}
          </span>
          <h2>{duel.collection.title}</h2>
        </div>
        <span className="duel-game">{duel.game_label} · {duel.difficulty_label}</span>
      </div>
      <div className="duel-players" aria-label="Participants du duel">
        <span>{duel.challenger.display_name}</span>
        <strong aria-hidden="true">VS</strong>
        <span>{duel.opponent.display_name}</span>
      </div>
      <dl className="duel-details">
        <div><dt>Cartes</dt><dd>{duel.cards_count}</dd></div>
        <div><dt>Échéance</dt><dd>{formatDate(deadline)}</dd></div>
        <div>
          <dt>Scores</dt>
          <dd>{duel.score_challenger ?? "—"} / {duel.score_opponent ?? "—"}</dd>
        </div>
      </dl>
      {result && <p className="duel-result">{result}</p>}
      <div className="duel-actions">
        {duel.can_accept && (
          <button className="button button-primary" type="button" disabled={busy} onClick={() => act(duel, "accept")}>
            Accepter
          </button>
        )}
        {duel.can_refuse && (
          <button className="button" type="button" disabled={busy} onClick={() => act(duel, "refuse")}>
            Refuser
          </button>
        )}
        {duel.can_play && (
          <button
            className="button button-primary"
            type="button"
            disabled={busy}
            onClick={() => navigate(`/series/${duel.collection.id}/${duel.game_slug}?duel=${duel.id}`)}
          >
            {duel.session_token ? "Reprendre" : "Jouer"}
          </button>
        )}
        {duel.can_abandon && (
          <button className="button button-danger" type="button" disabled={busy} onClick={() => act(duel, "abandon")}>
            Abandonner
          </button>
        )}
      </div>
    </article>
  );
}

function DuelCreation({ options, created }: { options: DuelOptions; created: () => void }) {
  const [opponentId, setOpponentId] = useState("");
  const [collectionId, setCollectionId] = useState("");
  const [game, setGame] = useState<DuelGame>("GAME_MEMO");
  const [difficulty, setDifficulty] = useState<MemoDifficulty>("SOFT_DIFFICULTY");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedCollection = useMemo(
    () => options.collections.find((item) => item.id === Number(collectionId)),
    [collectionId, options.collections],
  );
  const availableGames = selectedCollection?.games ?? [];

  useEffect(() => {
    if (availableGames.length && !availableGames.includes(game)) setGame(availableGames[0]!);
  }, [availableGames, game]);

  if (!options.can_create) {
    return (
      <div className="info-notice">
        <strong>Suivi enseignant</strong>
        <p>Vous pouvez consulter les duels des élèves de vos classes. Les invitations sont créées par les élèves.</p>
      </div>
    );
  }

  const submit = async () => {
    if (!opponentId || !collectionId || !availableGames.length) return;
    setBusy(true);
    setError(null);
    try {
      await createDuel({
        opponent_id: Number(opponentId),
        collection_id: Number(collectionId),
        game,
        difficulty,
      });
      setOpponentId("");
      setCollectionId("");
      created();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "L’invitation n’a pas pu être créée.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="duel-create" aria-labelledby="duel-create-title">
      <div>
        <p className="eyebrow">Nouveau défi</p>
        <h2 id="duel-create-title">Inviter un camarade</h2>
        <p>Le camarade doit appartenir à l’une de vos classes. Il disposera de 48 heures pour répondre.</p>
      </div>
      {options.opponents.length > 0 && options.collections.length > 0 ? (
        <div className="duel-form">
          <label>
            Adversaire
            <select value={opponentId} onChange={(event) => setOpponentId(event.target.value)}>
              <option value="">Choisir un camarade</option>
              {options.opponents.map((opponent) => <option key={opponent.id} value={opponent.id}>{opponent.display_name}</option>)}
            </select>
          </label>
          <label>
            Série
            <select value={collectionId} onChange={(event) => setCollectionId(event.target.value)}>
              <option value="">Choisir une série</option>
              {options.collections.filter((item) => item.games.length).map((collection) => <option key={collection.id} value={collection.id}>{collection.title}</option>)}
            </select>
          </label>
          <label>
            Mode
            <select value={game} disabled={!availableGames.length} onChange={(event) => setGame(event.target.value as DuelGame)}>
              {availableGames.map((value) => <option key={value} value={value}>{value.replace("GAME_", "")}</option>)}
            </select>
          </label>
          <label>
            Difficulté
            <select value={difficulty} onChange={(event) => setDifficulty(event.target.value as MemoDifficulty)}>
              {options.difficulties.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <button className="button button-primary" type="button" disabled={busy || !opponentId || !collectionId || !availableGames.length} onClick={() => void submit()}>
            {busy ? "Envoi…" : "Envoyer l’invitation"}
          </button>
        </div>
      ) : (
        <div className="empty-state"><strong>Aucun adversaire ou série disponible</strong><p>Rejoignez une classe et vérifiez qu’une série publiée possède un mode de jeu actif.</p></div>
      )}
      {error && <p className="game-error">{error}</p>}
    </section>
  );
}

export function DuelsPage() {
  const [payload, setPayload] = useState<DuelListResponse | null>(null);
  const [options, setOptions] = useState<DuelOptions | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [duels, nextOptions] = await Promise.all([fetchDuels(), fetchDuelOptions()]);
      setPayload(duels);
      setOptions(nextOptions);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Les duels sont indisponibles.");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const act = async (duel: DuelItem, action: "accept" | "refuse" | "abandon") => {
    if (action === "abandon" && !window.confirm("Abandonner ce duel ? Votre adversaire sera déclaré vainqueur.")) return;
    setBusyId(duel.id);
    setError(null);
    try {
      await performDuelAction(duel.id, action);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "L’action n’a pas pu être enregistrée.");
    } finally {
      setBusyId(null);
    }
  };

  const readNotifications = async () => {
    await markDuelNotificationsRead();
    await load();
  };

  return (
    <section className="duels-page">
      <button className="back-button" type="button" onClick={() => navigate("/")}>← Retour au tableau de bord</button>
      <div className="duels-heading">
        <div><p className="eyebrow">Défis pédagogiques</p><h1>Mes duels</h1><p className="lead">Même série, mêmes cartes et même ordre : seul votre résultat fera la différence.</p></div>
        {payload && payload.unread_notifications > 0 && <button className="notification-chip" type="button" onClick={() => void readNotifications()}>{payload.unread_notifications} notification{payload.unread_notifications > 1 ? "s" : ""} · marquer comme lues</button>}
      </div>
      {error && <div className="info-notice" role="alert"><strong>Le module Duel est momentanément indisponible</strong><p>{error}</p><button className="button" type="button" onClick={() => void load()}>Réessayer</button></div>}
      {payload?.notifications.length ? <aside className="duel-notifications" aria-label="Notifications de duel">{payload.notifications.slice(0, 5).map((notification) => <p className={notification.read ? "read" : ""} key={notification.id}><span aria-hidden="true">⚡</span>{notification.message}<time dateTime={notification.created_at}>{formatDate(notification.created_at)}</time></p>)}</aside> : null}
      {options && <DuelCreation options={options} created={() => void load()} />}
      <div className="duel-list">
        {payload === null && !error ? <div className="collection-loading">Chargement des duels…</div> : payload?.duels.length ? payload.duels.map((duel) => <DuelCard key={duel.id} duel={duel} busy={busyId === duel.id} act={(item, action) => void act(item, action)} />) : payload ? <div className="empty-state"><strong>Aucun duel pour le moment</strong><p>Créez une invitation ou attendez qu’un camarade vous défie.</p></div> : null}
      </div>
    </section>
  );
}
